import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import {
  getSingleShipmentLoadItem,
  resetSingleShipmentLoadItem,
} from "../../features/loadList/loadListSlice";
import PageTopTitle from "../../components/shared/PageTopTitle";
import GivenMapSection from "../../components/load/mainSections/GivenMapSection";
import { useNavigate } from "react-router-dom";
import loadIcon from "../../images/svg/topPages/load.svg"
import unloadIcon from "../../images/svg/topPages/unload.svg"
import loadMobile from "../../images/svg/topPages/load-mobile.svg"
import truckMobile from "../../images/svg/topPages/truck-mobile.svg"
import paketlemeTypes from "../../utils/yukOptions/paketlemeTypesOptions";
import formatConverter from "../../helper/formatConverter";
import yayinlamaSecenekleriOptions from "../../utils/yukOptions/yayinlamaSecenekleriOptions";
import SmallSpinner from "../../components/shared/spinners/SmallSpinner";
import { getRoutePointDisplayName } from "../../helper/shipment/safeShipmentListItem";

const Container = styled.div`
  width: 100%;

  .wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    .map-container {
        width: 100%;
        height: 300px;
        display: flex;
        flex-direction: column;

        .map-side {
            width: 100%;
            height: 300px;
            border-radius: 10px;
        }
    }
    .content {
      display: flex;
      flex-direction: column;
      gap: 60px;
      margin: 0 30px;
      margin-top: 30px;
        .content-item{
            font-size: var(--font-size-xs);
            font-weight: var(--font-weight-md);
        }
        .title{
            font-size: var(--font-size-md);
            font-weight: var(--font-weight-lg);
        }

        .load-unload{
            display: flex;
            gap: 20px;
            height: 100%;
            margin-top: 20px;
            margin-bottom: 30px;
            .x-seperator{
                height: auto;
                border: 1px solid;
                border-color: ${({ theme }) => theme.mainBorderColor};
            }
            .load-side{
                width: 50%;
                height: 100%;
                .loads, .route-list{
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                .route-list {
                    padding: 12px;
                    border: 1px solid ${({ theme }) => theme.mainBorderColor};
                    border-radius: 8px;
                }
            }
        }

        .operation-info {
            display: flex;
            flex-direction: column;
            gap: 5px;
            margin-top: 3px;
            padding-top: 9px;
            border-top: 1px dashed ${({ theme }) => theme.mainBorderColor};
            font-size: 12px;
            line-height: 1.45;
        }

        .operation-info.finished {
            color: var(--color-main);
        }

        .operation-info strong {
            margin-right: 4px;
        }

        .driver-history {
            width: 100%;
            padding: 20px;
            border: 1px solid ${({ theme }) => theme.mainBorderColor};
            border-radius: 10px;
        }

        .driver-history-title {
            margin: 0 0 14px;
            font-size: var(--font-size-md);
            font-weight: var(--font-weight-lg);
        }

        .history-list {
            display: flex;
            flex-direction: column;
        }

        .history-row {
            display: grid;
            grid-template-columns: minmax(160px, 1.2fr) minmax(150px, 1fr) minmax(180px, 1.2fr);
            gap: 14px;
            padding: 12px 0;
            border-top: 1px solid ${({ theme }) => theme.mainBorderColor};
            font-size: 13px;
        }

        .history-driver,
        .history-release {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }

        .history-secondary {
            color: ${({ theme }) => theme.secondaryTextColor};
            font-size: 11px;
            overflow-wrap: anywhere;
        }

        .active-session {
            color: var(--color-main);
            font-weight: var(--font-weight-lg);
        }

        .tracking-status {
            padding: 20px;
            text-align: center;
            color: ${({ theme }) => theme.secondaryTextColor};
            font-size: 13px;
        }

        .detail-container{
            display: flex;
            gap: 20px;
            .detail-side{
                width: 50%;
                display: flex;
                flex-direction: column;
                gap: 20px;
                .detail{
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    .detail-img{
                        height: 30px;
                    }
                }
            }
           
        }
    }
  }
  .bold{
    border-bottom: 1px solid;
    border-color: ${({ theme }) => theme.mainBorderColor};
    width: fit-content;
  }
  .vehicle{
    padding-left: 10px;
  }

  @media (max-width: 850px) {
    .wrapper .content {
      gap: 30px;
      margin: 20px;

      .load-unload,
      .detail-container {
        flex-direction: column;

        .x-seperator { display: none; }
        .load-side,
        .detail-side { width: 100%; }
      }

      .history-row { grid-template-columns: 1fr; }
    }

    .vehicle { padding-left: 0; }
  }
`;

const unwrapCollection = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.$values)) return value.$values;
  return [];
};

const formatUtcDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getRoutePointActor = (routePoint, prefix) => {
  const actorDetails = [
    routePoint?.[`${prefix}ByUserName`],
    routePoint?.[`${prefix}ByUserPhoneNumber`],
    routePoint?.[`${prefix}ByCompany`],
  ].filter(Boolean);

  return actorDetails.join(" · ") || routePoint?.[`${prefix}ByUserId`] || null;
};

const RoutePointOperationInfo = ({ routePoint }) => {
  const startedBy = getRoutePointActor(routePoint, "started");
  const updatedBy = getRoutePointActor(routePoint, "updated");
  const startedAt = formatUtcDate(routePoint?.routePointStartDate);
  const updatedAt = formatUtcDate(routePoint?.routePointEndDate);

  if (!startedBy && !updatedBy && !startedAt && !updatedAt) return null;

  return (
    <>
      {(startedBy || startedAt) && (
        <div className="operation-info">
          {startedBy && <div><strong>İşlemi başlatan:</strong>{startedBy}</div>}
          {startedAt && <div><strong>Başlangıç:</strong>{startedAt}</div>}
        </div>
      )}
      {(updatedBy || updatedAt) && (
        <div className="operation-info finished">
          {updatedBy && <div><strong>İşlemi bitiren:</strong>{updatedBy}</div>}
          {updatedAt && <div><strong>Bitiş:</strong>{updatedAt}</div>}
        </div>
      )}
    </>
  );
};

const VehicleDriverHistory = ({ history, isLoading, error }) => (
  <section className="driver-history">
    <h3 className="driver-history-title">Taşıma Seti Kullanım Geçmişi</h3>
    {isLoading ? (
      <div className="tracking-status">
        <SmallSpinner size="28px" color="#fff" bgColor="#E85446" />
      </div>
    ) : error ? (
      <div className="tracking-status">{error}</div>
    ) : history.length === 0 ? (
      <div className="tracking-status">Bu sevkiyat için taşıma seti teslim alma kaydı bulunmuyor.</div>
    ) : (
      <div className="history-list">
        {history.map((session) => {
          const id = session?.id ?? session?.Id;
          const driverUserId = session?.driverUserId ?? session?.DriverUserId;
          const driverName = session?.driverName ?? session?.DriverName;
          const driverPhoneNumber =
            session?.driverPhoneNumber ?? session?.DriverPhoneNumber;
          const claimedAtUtc = session?.claimedAtUtc ?? session?.ClaimedAtUtc;
          const releasedAtUtc = session?.releasedAtUtc ?? session?.ReleasedAtUtc;
          const releaseReason = session?.releaseReason ?? session?.ReleaseReason;

          return (
            <div className="history-row" key={id ?? `${driverUserId}-${claimedAtUtc}`}>
              <div className="history-driver">
                <strong>{driverName || driverPhoneNumber || driverUserId || "Bilinmeyen sürücü"}</strong>
                {driverName && driverPhoneNumber && (
                  <span className="history-secondary">{driverPhoneNumber}</span>
                )}
                {driverUserId && (
                  <span className="history-secondary">Kullanıcı ID: {driverUserId}</span>
                )}
              </div>
              <div>
                <strong>Devralma:</strong> {formatUtcDate(claimedAtUtc) || "-"}
              </div>
              <div className={`history-release ${releasedAtUtc ? "" : "active-session"}`}>
                <span>
                  {releasedAtUtc
                    ? `Bırakma: ${formatUtcDate(releasedAtUtc) || "-"}`
                    : "Taşıma seti halen bu sürücüde"}
                </span>
                {releaseReason && (
                  <span className="history-secondary">Bırakma nedeni: {releaseReason}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </section>
);

const DetailShipments = () => {
  const dispatch = useDispatch();
  const { theme } = useSelector((state) => state.layout);
  const {
    loadItemData: selectedShipment,
    singleShipmentLoadItem,
    isLoadingGetSingleShipmentLoadItem,
    singleShipmentLoadItemError,
  } = useSelector((state) => state.loadList);
  const navigate = useNavigate();
  const hasDetailedShipment =
    singleShipmentLoadItem?.id === selectedShipment?.id;
  const loadItemData = hasDetailedShipment
    ? {
        ...selectedShipment,
        ...singleShipmentLoadItem,
        waitinLoad: {
          ...selectedShipment?.waitinLoad,
          ...singleShipmentLoadItem?.waitinLoad,
        },
      }
    : selectedShipment;
  const routePoints = unwrapCollection(loadItemData?.routePoints);
  const vehicleDriverHistory = hasDetailedShipment
    ? unwrapCollection(singleShipmentLoadItem?.vehicleDriverHistory)
    : [];

  useEffect(() => {
    if (!selectedShipment?.id) return undefined;
    dispatch(resetSingleShipmentLoadItem());
    dispatch(getSingleShipmentLoadItem(selectedShipment.id));

    return () => dispatch(resetSingleShipmentLoadItem());
  }, [dispatch, selectedShipment?.id]);

  useEffect(() => {
    if (!selectedShipment) navigate("/yukler-listesi", { replace: true });
  }, [navigate, selectedShipment]);

  const route = useMemo(
    () => routePoints.map((innerItem, index) => {
      if (index === 0 || index === routePoints.length - 1) {
        return {
          lat: parseFloat(innerItem.lat),
          lng: parseFloat(innerItem.lng),
        };
      } else {
        return {
          location: {
            lat: parseFloat(innerItem.lat),
            lng: parseFloat(innerItem.lng),
          },
        };
      }
    }),
    [routePoints]
  );

  if (!selectedShipment) return null;

  return (
    <Container theme={theme}>
      <div className="wrapper">
        <PageTopTitle title={loadItemData?.pageTitle} position="sticky" />
        <div className="map-container">
            <div className="map-side">
                <GivenMapSection
                    route={route}
                    defaultLocation={{ lat: 38.75, lng: 33.75 }}
                    zoom={5}
                />
            </div>
        </div>
        <div className="content">
          <div className="load-unload">
            <div className="load-side">
                <img src={loadIcon} alt="" />
                <div className="loads">
                    <p className="title">Yükleme</p>
                    <div className="content-item">{loadItemData?.waitinLoad?.planningLoadDate} - {loadItemData?.waitinLoad?.planningLoadTime}</div>
                    <div className="route">
                        {routePoints
                            .filter((item) => item.loadOrUnload === 0)
                            .map((routeItem, i) => (
                            <div className="route-list" key={routeItem.id ?? `load-${routeItem.order}-${i}`}>
                                <div className="content-item bold">
                                    {i + 1}. {getRoutePointDisplayName(routeItem)}
                                </div>
                                <div className="content-item">{routeItem.location}</div>
                                <div className="content-item">{routeItem.address}</div>
                                {unwrapCollection(routeItem.loadMethods).length > 0 &&
                                    <div className="content-item">
                                        {unwrapCollection(routeItem.loadMethods).map((item) => item.name).join(", ")}
                                    </div>
                                }
                                {routeItem.routePointPersonName &&
                                    <div className="content-item">
                                        {routeItem.routePointPersonName}
                                    </div>
                                }
                                {routeItem.routePointPersonPhoneNumber && 
                                    <div className="content-item">
                                        {routeItem.routePointPersonPhoneNumber}
                                    </div>
                                }
                                <RoutePointOperationInfo routePoint={routeItem} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            
            <div className="x-seperator" />

            <div className="load-side">
                <img src={unloadIcon} alt="" />
                <div className="loads">
                    <p className="title">Boşaltma</p>
                    <div className="content-item">{loadItemData?.waitinLoad?.planningUnloadDate} - {loadItemData?.waitinLoad?.planningUnloadTime}</div>
                    <div className="route">
                        {routePoints
                            .filter((item) => item.loadOrUnload === 1)
                            .map((routeItem, i) => (
                            <div className="route-list" key={routeItem.id ?? `unload-${routeItem.order}-${i}`}>
                                <div className="content-item bold">
                                    {i + 1}. {getRoutePointDisplayName(routeItem)}
                                </div>
                                <div className="content-item">{routeItem.location}</div>
                                <div className="content-item">{routeItem.address}</div>
                                {unwrapCollection(routeItem.loadMethods).length > 0 &&
                                    <div className="content-item">
                                        {unwrapCollection(routeItem.loadMethods).map((item) => item.name).join(", ")}
                                    </div>
                                }
                                {routeItem.routePointPersonName &&
                                    <div className="content-item">
                                        {routeItem.routePointPersonName}
                                    </div>
                                }
                                {routeItem.routePointPersonPhoneNumber && 
                                    <div className="content-item">
                                        {routeItem.routePointPersonPhoneNumber}
                                    </div>
                                }
                                <RoutePointOperationInfo routePoint={routeItem} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
          </div>

          <VehicleDriverHistory
            history={vehicleDriverHistory}
            isLoading={isLoadingGetSingleShipmentLoadItem && !hasDetailedShipment}
            error={!hasDetailedShipment ? singleShipmentLoadItemError : null}
          />

          <div className="detail-container">
            <div className="detail-side">
                <div className="detail">
                    <div className="detail-img">
                        <img src={loadMobile} alt="" />
                    </div>
                    <p className="title">Yük</p>
                    {loadItemData.senderName &&
                        <div className="content-item">{loadItemData.senderName}</div>
                    }
                    <div className="content-item">
                        {loadItemData.isParsiyel ? "LTL(Parsiyel)" : "FTL(Komple Araç)"}
                    </div>
                    {loadItemData.LoadUnitId === 3 ? (
                        <div className="content-item">
                        Basar Tonaj{" "}
                        {loadItemData.loadBulk > 0 &&
                            ` - ${loadItemData.loadBulk} ${loadItemData.loadBulk > 0 && "m3"}`}
                        </div>
                    ) : (
                        <div className="content-item">
                        {loadItemData.loadQuantity} {loadItemData.loadUnit}{" "}
                        {loadItemData.loadBulk > 0 &&
                            ` - ${loadItemData.loadBulk} ${loadItemData.loadBulk > 0 && "m3"}`}
                        </div>
                    )}
                    <div className="content-item">{loadItemData.loadType}</div>
                    {loadItemData.loadDescription && <div className="content-item">{loadItemData.loadDescription}</div>}
                    {unwrapCollection(loadItemData?.packingingTypes).map((item) => {
                        return (
                            <div className="content-item" key={item.id ?? item.packagingUnitId}>
                                {paketlemeTypes?.find((innerItem) => {
                                    return innerItem?.id === item?.packagingUnitId
                                })?.name}
                            </div>
                        )
                    })}
                    <div className="content-item">Sigorta Değeri: {loadItemData?.loadValue} TL</div>
                </div>
                
                <div className="detail">
                    <div className="title">Diğer Bilgiler</div>
                    <div className="content-item">
                        {yayinlamaSecenekleriOptions?.find(
                            (yItem) => yItem?.id === loadItemData?.waitinLoad?.publishingTypeId
                        )?.text}
                    </div>
                    <div className="content-item">
                        {formatConverter(loadItemData.distance, 0)} Km
                    </div>
                    <div className="content-item">{loadItemData?.projectName}</div>
                </div>
            </div>
            <div className="detail-side vehicle">
                <div className="detail">
                    <div className="detail-img">
                        <img src={truckMobile} alt="" />
                    </div>
                    <p className="title">Araç</p>
                    <div className="content-item">{loadItemData?.waitinLoad?.truckQuantity} Adet</div>
                    <div className="content-item">{loadItemData?.waitinLoad?.truckType}</div>
                    <div className="content-item">{loadItemData?.waitinLoad?.groundType}</div>
                    {loadItemData?.driverName && (
                      <div className="content-item">Sürücü: {loadItemData.driverName}</div>
                    )}
                    {loadItemData?.truckPlateNumber && (
                      <div className="content-item">Çekici: {loadItemData.truckPlateNumber}</div>
                    )}
                    {loadItemData?.trailerPlateNumber && (
                      <div className="content-item">Dorse: {loadItemData.trailerPlateNumber}</div>
                    )}
                    {loadItemData?.containerNumber && (
                      <div className="content-item">Konteyner: {loadItemData.containerNumber}</div>
                    )}
                    {loadItemData?.sealNumber && (
                      <div className="content-item">Mühür: {loadItemData.sealNumber}</div>
                    )}
                </div>
            </div>
          </div>
        </div>
        
      </div>
    </Container>
  );
};

export default DetailShipments;
